/**
 * TTS 文本规范化工具
 *
 * 作用：把屏幕上展示的文本（含数字、单位符号、技术缩写）
 * 转换为适合语音合成（Text-To-Speech）自然朗读的纯中文文本。
 *
 * 设计原则：
 * 1. 屏幕上仍然显示原始的 m³、m3/s、km²、MW 等符号，方便阅读；
 * 2. 语音输出时把这些符号读成"立方米""立方米每秒""平方千米""兆瓦"等；
 * 3. 精简冗长的同步指标列表，只播报关键信息，避免语音过长；
 * 4. 标点转为适合朗读的停顿（顿号→逗号，分号→逗号）。
 */

/* ---------- 单位符号 → 中文朗读映射 ----------
 * 顺序很重要：长串优先匹配，避免 "m3/s" 被 "m3" 提前吃掉。
 */
const UNIT_REPLACEMENTS = [
  // 复合流量单位（必须放在最前）
  { re: /m\s*3\s*\/\s*s/gi, to: '立方米每秒' },
  { re: /m³\s*\/\s*s/gi, to: '立方米每秒' },
  { re: /m\^3\s*\/\s*s/gi, to: '立方米每秒' },
  // 体积 / 库容
  { re: /亿\s*m\s*3/gi, to: '亿立方米' },
  { re: /亿\s*m³/gi, to: '亿立方米' },
  { re: /万\s*m\s*3/gi, to: '万立方米' },
  { re: /万\s*m³/gi, to: '万立方米' },
  { re: /m\s*3/gi, to: '立方米' },
  { re: /m³/gi, to: '立方米' },
  { re: /m\^3/gi, to: '立方米' },
  // 面积
  { re: /km\s*2/gi, to: '平方千米' },
  { re: /km²/gi, to: '平方千米' },
  { re: /km\^2/gi, to: '平方千米' },
  // 含沙量
  { re: /kg\s*\/\s*m\s*3/gi, to: '千克每立方米' },
  { re: /kg\s*\/\s*m³/gi, to: '千克每立方米' },
  // 电力
  { re: /\bMW\b/g, to: '兆瓦' },
  { re: /\bkWh\b/gi, to: '千瓦时' },
  { re: /亿\s*kWh/gi, to: '亿千瓦时' },
  { re: /万\s*kWh/gi, to: '万千瓦时' },
  // 长度 / 水位
  { re: /\bmm\b/g, to: '毫米' },
  { re: /\bcm\b/g, to: '厘米' },
  { re: /\bkm\b/g, to: '千米' },
  // 浊度
  { re: /\bNTU\b/g, to: '浊度单位' },
  // 水质等级
  { re: /grade\s*([ⅠⅡⅢⅣⅤ1-5])/gi, to: '$1类' },
  // pH
  { re: /\bpH\b/g, to: '酸碱度' },
]

/* ---------- 数字朗读优化 ----------
 * 小数点后保留 1-2 位即可，避免 "六百一十六点四二零" 这种冗长读法。
 * 这里只做轻量处理：把多余的小数尾零去掉。
 */
function trimTrailingZeros(text) {
  return text.replace(/(\d+\.\d*?)0+(?=\D|$)/g, '$1').replace(/(\d+)\.(?=\D|$)/g, '$1')
}

/* ---------- 关键信息精简 ----------
 * 当文本很长（比如监测同步指标列表）时，只保留前面的核心结论，
 * 避免语音合成读一大串数字列表。
 */
const TRIM_PATTERNS = [
  // 去掉"同步指标："后面的长列表
  { re: /[，,。；;]\s*同步指标[：:].*$/s, to: '' },
  // 去掉"来源："后面的标注
  { re: /[，,。；;]\s*来源[：:].*$/s, to: '' },
  // 去掉"时间："后面的时间戳
  { re: /[，,。；;]\s*时间[：:].*$/s, to: '' },
  // 去掉"参数来源"说明
  { re: /[。；]\s*参数来源.*$/s, to: '。' },
  // 去掉"坐标说明"说明
  { re: /[。；]\s*坐标说明.*$/s, to: '。' },
  // 去掉"当前联动逻辑为："后面的超长描述
  { re: /当前联动逻辑为[：:].*$/s, to: '' },
  // 去掉"GIS 图层包含"后面的超长描述
  { re: /GIS\s*图层包含.*$/s, to: '' },
]

/**
 * 把屏幕展示文本转换为适合 TTS 朗读的文本。
 * @param {string} raw 原始文本
 * @returns {string} 规范化后的朗读文本
 */
export function normalizeForTTS(raw) {
  if (!raw) return ''
  let text = String(raw)

  // 1) 先精简冗长段落（在单位替换之前，因为模式里有英文/符号）
  for (const p of TRIM_PATTERNS) {
    text = text.replace(p.re, p.to)
  }

  // 2) 单位符号替换
  for (const u of UNIT_REPLACEMENTS) {
    text = text.replace(u.re, u.to)
  }

  // 3) 数字尾零精简
  text = trimTrailingZeros(text)

  // 4) 标点优化：顿号→逗号，分号→逗号，多个连续标点合并
  text = text.replace(/、/g, '，').replace(/；/g, '，').replace(/([，。！？])\1+/g, '$1')

  // 5) 去掉首尾空白和多余空格
  text = text.replace(/\s+/g, ' ').trim()

  // 6) 如果文本仍然过长（>120字），截断到第一个句号之后
  if (text.length > 120) {
    const firstPeriod = text.indexOf('。')
    if (firstPeriod > 0 && firstPeriod < 120) {
      text = text.slice(0, firstPeriod + 1)
    } else {
      text = text.slice(0, 117) + '。'
    }
  }

  return text
}

export default normalizeForTTS
